import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { JourneyComponent } from 'shared/components/JourneyComponent';
import { ProfileIdentitySearch } from 'features/profiles/ProfileIdentitySearch';
import { useTalosData } from 'shared/context/TalosDataContext';
import { Badge } from 'features/catalog/useBadges';
import { JOURNEYS } from 'features/catalog/badge-journeys';
import { talosBadgesLogoURL } from 'config/constants';
import { getJourneysSortedByClaimRate } from 'features/catalog/rarity';
import './BadgeGalleryView.css';
import './JourneysGalleryView.css';

type JourneyFilter = 'all' | 'common' | 'rare' | 'legendary';

interface JourneysGalleryViewProps {
  selectedProfileId?: string | null;
  onSelectBadge: (badge: Badge) => void;
  onSelectProfile: (profileId: string) => void;
  onClearProfile?: () => void;
}

const journeyMatchesFilter = (rarityName: string, filter: JourneyFilter): boolean => {
  if (filter === 'all') return true;
  if (filter === 'common') return rarityName === 'common';
  if (filter === 'rare') return rarityName === 'rare';
  return rarityName === 'legendary' || rarityName === 'mythic';
};

export const JourneysGalleryView: React.FC<JourneysGalleryViewProps> = ({
  selectedProfileId = null,
  onSelectBadge,
  onSelectProfile,
  onClearProfile,
}) => {
  const { badgesMap, profilesMap } = useTalosData();
  const [filter, setFilter] = useState<JourneyFilter>('all');
  const location = useLocation();
  const navigate = useNavigate();
  const hashJourneyId = location.hash.replace(/^#/, '');

  const sortedJourneys = useMemo(() => getJourneysSortedByClaimRate(profilesMap.values()), [profilesMap]);

  const visible = useMemo(
    () => sortedJourneys.filter((journey) => journeyMatchesFilter(journey.rarity, filter)),
    [sortedJourneys, filter]
  );

  const clearJourneyHash = () => {
    if (!location.hash) return;
    navigate({ pathname: location.pathname, search: location.search, hash: '' }, { replace: true });
  };

  // Deep-link: unhide target if needed, then scroll once. Filter clicks clear the hash first
  // so they do not re-trigger this scroll.
  useEffect(() => {
    if (!hashJourneyId) return;
    const journey = JOURNEYS.find((j) => j.id === hashJourneyId);
    if (!journey) return;
    if (!journeyMatchesFilter(journey.rarity, filter)) {
      setFilter('all');
      return;
    }

    const timeout = window.setTimeout(() => {
      const el = document.getElementById(hashJourneyId);
      const scroller = document.querySelector('.app-shell-main');
      if (!el) return;
      if (scroller instanceof HTMLElement) {
        const top = el.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop - 24;
        scroller.scrollTo({ top, behavior: 'smooth' });
      } else {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 80);

    return () => window.clearTimeout(timeout);
  }, [hashJourneyId, filter]);

  const chips: { id: JourneyFilter; label: string }[] = [
    { id: 'all', label: 'All' },
    { id: 'common', label: 'Common' },
    { id: 'rare', label: 'Rare' },
    { id: 'legendary', label: 'Legendary' },
  ];

  return (
    <div className="gallery-page journeys-gallery-page">
      <header className="gallery-header">
        <ProfileIdentitySearch
          selectedProfileId={selectedProfileId}
          onSelectProfile={(id) => {
            clearJourneyHash();
            onSelectProfile(id);
          }}
          onClear={() => {
            clearJourneyHash();
            onClearProfile?.();
          }}
          avatarSrc={talosBadgesLogoURL}
        >
          <h1 className="gallery-title">Journeys Gallery</h1>
          <p className="gallery-subtitle">{JOURNEYS.length} journeys</p>
        </ProfileIdentitySearch>
      </header>

      <div className="gallery-nav" role="tablist" aria-label="Journey filters">
        {chips.map((chip) => (
          <button
            key={chip.id}
            type="button"
            role="tab"
            aria-selected={filter === chip.id}
            className={`gallery-nav-chip ${filter === chip.id ? 'active' : ''}`}
            onClick={() => {
              clearJourneyHash();
              setFilter(chip.id);
            }}
          >
            {chip.label}
          </button>
        ))}
      </div>

      <div className="journeys-light-grid">
        {visible.map((journey) => (
          <JourneyComponent
            key={journey.id}
            journey={journey}
            badgesMap={badgesMap}
            badgeIDs={[]}
            display
            onClickBadge={(badge) => {
              clearJourneyHash();
              onSelectBadge(badge);
            }}
          />
        ))}
        {visible.length === 0 ? <p className="journeys-gallery-empty">Nothing matches this filter.</p> : null}
      </div>
    </div>
  );
};
